/* eslint-disable */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { updatePlanModal } from 'store/user';
import {
  Row,
  Col,
  Tabs,
  Space,
  Typography,
  Tag,
  Tooltip,
  Popover,
  Skeleton,
  Carousel,
  Button,
  Spin
} from 'antd';
import { REFERRAL_SETTING } from 'pages/CustomReportsListPage/const';
import Alert from 'components/Alert';
import UserInfoCard from 'components/UserInfoCard';
import merchantAPI from 'services/merchants';
import KycCard from 'components/KycCard';
import ProductCard from 'components/ProductCard';
import EbzCarousel from 'components/EbzCarousel';
import CombineCard from 'components/CombineCard';
import { MerchantDetailTabMenu, slabType } from './const';
import MerchantDetailsSkeleton from './MerchantDetailsSkeleton';
import ProductTab from './ProductTab';
import SettingTab from './SettingTab';

const MenuTabBarWrapper = styled.div`
  && {
    .ant-tabs-tab {
      padding: 12px 10px;
      font-size: 17px;
    }

    .ant-tabs-nav::before {
      border-bottom: 2px solid rgba(5, 5, 5, 0.06);
    }
  }
`;

const renderTabBarMenu = (props, DefaultTabBar) => {
  return (
    <MenuTabBarWrapper>
      <DefaultTabBar {...props} />
    </MenuTabBarWrapper>
  );
};

function MerchantDetailsPage() {
  const [hasUserInfo, setHasUserInfo] = useState(false);
  const [userInfo, setUserInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [markupData, setMarkupData] = useState({});
  const [settingData, setSettingData] = useState({});

  const [searchParams] = useSearchParams();
  const params = useParams();
  const submerchantId = searchParams.get('submerchant');
  const referralSource = searchParams.get('app', 1);

  // redux state
  const isKycUpdateAllowed = useSelector((state) => state.user.planPermissions?.can_update_kyc);
  const isUpdateSubmerchantKyc = useSelector(
    (state) => state.user.planPermissions?.can_update_submerchant_kyc
  );
  const isMarkupAllowed = useSelector((state) => state.user.planPermissions?.can_view_markup);
  const isRefferalAccess = useSelector((state) => state.user.planPermissions?.can_view_referrals);
  const user = useSelector((state) => state.user);
  /* eslint no-underscore-dangle: ["error", { "allow": ["_id"] }] */
  const partnerId = user?.currentUser?.merchant_id;
  const [tabMenuItem, setTabMenuItem] = useState([]);
  const firstValidTabKey = MerchantDetailTabMenu.find((item) =>
    tabMenuItem.some((tab) => tab.key === item.key)
  )?.key;

  const [currentTabActiveKey, setCurrentTabActiveKey] = useState(firstValidTabKey);

  // functions for api calls
  const generateInitialValues = (products, slabKeys) => {
    const values = {};

    products.forEach((productItem) => {
      const productKey = productItem?.product_name;
      values[productKey] = {};

      productItem?.configuration.forEach((mode) => {
        const modeKey = mode?.mode_type;
        values[productKey][modeKey] = {
          is_enable: mode?.is_enable
        };

        mode?.data.forEach((typeObj) => {
          const typeKey = typeObj?.key;
          values[productKey][modeKey][typeKey] = {
            label: typeObj?.label,
            [slabKeys[0]]: {}
          };

          // Primary Slab
          typeObj?.range?.forEach((slabItem, idx) => {
            values[productKey][modeKey][typeKey][slabKeys[0]][idx] = {
              min_amount: slabItem?.min_amount,
              max_amount: slabItem?.max_amount,
              commercial_type: slabItem?.commercial_type,
              commission_type: slabItem?.commission_type,
              surcharge_flag: slabItem?.surcharge_flag,
              merchant_commercial: slabItem?.merchant_commercial,
              commercial_value: slabItem?.commercial_value,
              partner_commission: slabItem?.partner_commission,
              base_rate_type: slabItem?.base_rate_type,
              base_rate_value: slabItem?.base_rate_value,
              flat_fee: slabItem?.flat_fee
            };
          });

          // Differential Slab
          if (typeObj?.differential) {
            values[productKey][modeKey][typeKey][slabKeys[1]] = {
              label: typeObj?.differential_label
            };

            const differentialSection = values[productKey][modeKey][typeKey][slabKeys[1]];

            Object.entries(typeObj.differential).forEach(([brandKey, brands]) => {
              differentialSection[brandKey] = {};
              brands.forEach((slabBrand, entryIndex) => {
                differentialSection[brandKey][entryIndex] = {
                  min_amount: slabBrand?.min_amount,
                  max_amount: slabBrand?.max_amount,
                  commercial_type: slabBrand?.commercial_type,
                  surcharge_flag: slabBrand?.surcharge_flag,
                  commission_type: slabBrand?.commission_type,
                  merchant_commercial: slabBrand?.merchant_commercial,
                  partner_commission: slabBrand?.partner_commission,
                  flat_fee: slabBrand?.flat_fee
                };
              });
            });
          }
        });
      });
    });
    return values;
  };

  const buildTabMenuItems = (menuItems, { markupData, settingData, initialFormValues }) => {
    return menuItems.map((menuItem) => {
      // Product Tab
      if (menuItem.key === MerchantDetailTabMenu[0].key) {
        return {
          ...menuItem,
          children:
            markupData && Object.keys(markupData).length > 0 ? (
              <ProductTab products={markupData} initial={initialFormValues} />
            ) : (
              menuItem.children
            )
        };
      }

      // Setting Tab
      if (menuItem.key === MerchantDetailTabMenu[1].key) {
        return {
          ...menuItem,
          children:
            settingData && Object.keys(settingData).length > 0 ? (
              <SettingTab settings={settingData} />
            ) : (
              menuItem.children
            )
        };
      }

      return menuItem;
    });
  };

  const fetchMerchantInfo = async (payload) => {
    const response = await merchantAPI.info(payload);
    if (!response.success) {
      setHasUserInfo(false);
      Alert('something went wrong', 'Please contact support team for this', 'error');
    } else {
      setHasUserInfo(true);
      setUserInfo(response?.data ?? []);
    }
  };
  const handleTabChange = (e) => {
    setCurrentTabActiveKey(e);
  };

  const fetchInfo = () => {
    const request = {
      merchant_id: parseInt(params.id, 10)
    };
    if (submerchantId && referralSource) {
      request['submerchant_id'] = submerchantId;
      request['referral_source'] = parseInt(referralSource, 10);
    }
    fetchMerchantInfo(request);
  };

  const fetchMerchantMarkups = async (payload) => {
    const response = await merchantAPI.getMarkupConfigData(payload);

    if (!response.success) {
      Alert('something went wrong', response?.data?.error?.message, 'error');
      return;
    }

    if (response?.data?.length === 0) {
      console.log('no Markup data');
      return;
    }
    setMarkupData(response?.data ?? []);

    setTabMenuItem((prevTabMenu) => {
      // prevTabMenu contains the current array before this update
      return [
        ...prevTabMenu,
        {
          label: 'Rates',
          key: 1
        }
      ];
    });
  };
  const fetchMerchantSettings = async (payload) => {
    const response = await merchantAPI.merchantSettingData(payload);

    if (!response.success) {
      console.log('something went wrong');
      return;
    }
    setSettingData(response?.data ?? []);

    setTabMenuItem((prevTabMenu) => {
      // prevTabMenu contains the current array before this update
      return [
        ...prevTabMenu,
        {
          label: 'Settings',
          key: 2
        }
      ];
    });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      if (isMarkupAllowed) {
        const request = {
          merchant_id: parseInt(params.id, 10)
        };
        if (submerchantId && referralSource) {
          request['submerchant_id'] = submerchantId;
          request['referral_source'] = parseInt(referralSource, 10);
        }
        await fetchMerchantMarkups(request);
      }
      if (REFERRAL_SETTING.includes(partnerId)) {
        const request = {
          merchant_id: parseInt(params.id, 10)
        };
        if (submerchantId && referralSource) {
          request['submerchant_id'] = submerchantId;
          request['referral_source'] = parseInt(referralSource, 10);
        }
        await fetchMerchantSettings(request);
      }
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [isMarkupAllowed]);

  useEffect(() => {
    if (isRefferalAccess) {
      fetchInfo();
    }
  }, [isRefferalAccess]);
  const safeKey = (key) => {
    if (!key || typeof key !== 'string') return 'unknown';
    // Prevent prototype pollution
    const blocked = ['__proto__', 'constructor', 'prototype'];
    if (blocked.includes(key)) return `safe_${key}`;
    // Remove dangerous characters
    return key.replace(/[^a-zA-Z0-9_-]/g, '_');
  };

  useEffect(() => {
    if (!markupData || Object.keys(markupData).length === 0) return;
    const initialValues = generateInitialValues(markupData, slabType);

    setTabMenuItem((prev) =>
      buildTabMenuItems(prev, {
        markupData,
        initialFormValues: initialValues
      })
    );
  }, [markupData]);

  useEffect(() => {
    if (!settingData || Object.keys(settingData).length === 0) return;

    setTabMenuItem((prev) => buildTabMenuItems(prev, { settingData }));
  }, [settingData]);

  return (
    <div style={{ padding: '20px' }}>
      <Row gutter={[20, 20]}>
        <Col span={9}>
          {hasUserInfo ? (
            <UserInfoCard
              name={userInfo?.personal_details?.name ?? '-'}
              bankName={userInfo?.personal_details?.settlement_bank_name ?? '-'}
              city={userInfo?.personal_details?.city ?? '-'}
              mcc={userInfo?.personal_details?.merchant_category_code ?? '-'}
              accountNumber={userInfo?.personal_details?.settlement_account_number ?? '-'}
              email={userInfo?.personal_details?.email ?? '-'}
              phone={userInfo?.personal_details?.phone_number ?? '-'}
              mid={userInfo?.personal_details?.merchant_id ?? '-'}
              smid={userInfo?.personal_details?.submerchant_id ?? null}
              // address={userInfo?.personal_details?.address ?? '-'}
            />
          ) : (
            <MerchantDetailsSkeleton />
          )}
        </Col>
        <Col span={7}>
          {hasUserInfo === true ? (
            <CombineCard
              gtv={userInfo?.gtv_profit_card_details ?? {}}
              login={userInfo?.login_card_details ?? {}}
              isKycComplte={userInfo?.kyc_product_card_details?.is_kyc_complete ?? '-'}
            />
          ) : (
            <Row gutter={[20, 20]} style={{ maxWidth: '100%' }}>
              <Col
                span={24}
                style={{
                  background:
                    'transparent linear-gradient(119deg, #FFFFFF 35%, #E5FDFD 100%) 0% 0% no-repeat padding-box',
                  boxShadow: '0px 1px 6px #00000014',
                  borderRadius: '10px'
                }}
              >
                <Skeleton
                  active
                  paragraph={{
                    rows: 2
                  }}
                />
              </Col>
              <Col
                span={24}
                style={{
                  background:
                    'transparent linear-gradient(119deg, #FFFFFF 35%, #E5FDFD 100%) 0% 0% no-repeat padding-box',
                  boxShadow: '0px 1px 6px #00000014',
                  borderRadius: '10px'
                }}
              >
                <Skeleton
                  active
                  paragraph={{
                    rows: 2
                  }}
                />
              </Col>
            </Row>
          )}
        </Col>
        <Col
          span={8}
          style={{
            borderRadius: '13px'
          }}
        >
          {hasUserInfo ? (
            <EbzCarousel effect="fade">
              <KycCard
                bankStatus={userInfo?.kyc_product_card_details?.bank_status ?? '-'}
                businessStatus={userInfo?.kyc_product_card_details?.business_status ?? '-'}
                basicStatus={userInfo?.kyc_product_card_details?.basic_status ?? '-'}
                merchantId={userInfo?.personal_details?.merchant_id ?? null}
                submerchantId={submerchantId}
                referralSource={referralSource}
                isKycComplete={userInfo?.kyc_product_card_details?.is_kyc_complete ?? false}
                updateKyc={isKycUpdateAllowed || isUpdateSubmerchantKyc}
              />
              {/* <ProductCard
                products={userInfo?.kyc_product_card_details?.products ?? {}}
                merchantId={userInfo?.personal_details?.merchant_id ?? null}
                fetchMerchantInfo={fetchInfo}
                requestProduct={isProductRequestAllowed ?? undefined}
              /> */}
            </EbzCarousel>
          ) : (
            <Row
              gutter={[16, 16]}
              style={{
                boxShadow: 'rgb(0 0 0 / 6%) 0px 3px 6px 4px',
                borderRadius: '10px',
                backgroundColor: 'white'
              }}
            >
              <Col span={24}>
                <Skeleton
                  active
                  paragraph={{
                    rows: 4
                  }}
                />
              </Col>
            </Row>
          )}
        </Col>
      </Row>

      <Spin spinning={loading}>
        {tabMenuItem.length > 0 && (
          <div style={{ marginTop: '25px' }}>
            <Tabs
              activeKey={currentTabActiveKey}
              size="large"
              items={tabMenuItem}
              renderTabBar={renderTabBarMenu}
              onChange={handleTabChange}
            />
          </div>
        )}
      </Spin>
    </div>
  );
}

export default MerchantDetailsPage;
